/**
 * useWorkflowPermissions — Hook to check workflow permissions for UI gating.
 *
 * sl-za0v.10.3: Fetches the current user's permissions on a specific workflow
 * from the SWR API. Used by Automation Sidebar, Workflow Builder, and History Panel
 * to show/hide/disable UI elements based on RBAC.
 *
 * Returns loading state + individual permission booleans.
 *
 * The permissions the server confirmed stay on the device: without network
 * the builder keeps what was last confirmed, and the server still checks
 * every change it receives.
 */
import { WorkspaceService } from '@affine/core/modules/workspace';
import { WorkspaceLocalCacheService } from '@affine/core/modules/workspace-local-cache';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { defaultWorkspaceApiClient } from '../../cloud';

// =============================================================================
// Types
// =============================================================================

export interface WorkflowPermissions {
  canEdit: boolean;
  canRun: boolean;
  canViewResults: boolean;
  canDelete: boolean;
  canPublish: boolean;
  canManagePermissions: boolean;
}

const NO_PERMISSIONS: WorkflowPermissions = {
  canEdit: false,
  canRun: false,
  canViewResults: false,
  canDelete: false,
  canPublish: false,
  canManagePermissions: false,
};

const ALL_PERMISSIONS: WorkflowPermissions = {
  canEdit: true,
  canRun: true,
  canViewResults: true,
  canDelete: true,
  canPublish: true,
  canManagePermissions: true,
};

const DEVICE_NAMESPACE = 'workflow-permissions';

// =============================================================================
// Hook
// =============================================================================

export function useWorkflowPermissions(workflowId: string | null): {
  permissions: WorkflowPermissions;
  loading: boolean;
  refresh: () => void;
} {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const device = useService(WorkspaceLocalCacheService);
  const [permissions, setPermissions] =
    useState<WorkflowPermissions>(NO_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  // The workflow the server last confirmed the permissions for.
  const confirmedFor = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (!workflowId) {
      // No workflow selected yet — allow all actions so the user can select/create one.
      // Backend enforces actual permissions once a workflow is chosen.
      setPermissions(ALL_PERMISSIONS);
      setLoading(false);
      return;
    }

    setLoading(true);
    // Without an answer the permissions stay what the server last confirmed
    // for this workflow, and are none for another one.
    const unanswered = () =>
      setPermissions(current =>
        confirmedFor.current === workflowId ? current : NO_PERMISSIONS
      );
    try {
      const path = `/api/workspace/${workspaceId}/workflow/${workflowId}/my-permissions`;
      const res = await defaultWorkspaceApiClient.request({
        workspaceId,
        path,
        category: 'permissions',
        key: `GET:${path}`,
        mode: 'background',
        priority: 'visible',
      });

      if (!res.ok) {
        unanswered();
        return;
      }

      const data: { permissions: string[] } = await res.json();
      const perms = new Set(data.permissions);
      const confirmed: WorkflowPermissions = {
        canEdit: perms.has('workflow.edit'),
        canRun: perms.has('workflow.run'),
        canViewResults: perms.has('workflow.view_results'),
        canDelete: perms.has('workflow.delete'),
        canPublish: perms.has('workflow.publish'),
        canManagePermissions: perms.has('workflow.manage_permissions'),
      };

      confirmedFor.current = workflowId;
      setPermissions(confirmed);
      await device.write(DEVICE_NAMESPACE, workflowId, confirmed);
    } catch {
      // No network: what the server confirmed before, in this session or
      // on this device.
      if (confirmedFor.current === workflowId) return;
      const stored = await device.read<WorkflowPermissions>(
        DEVICE_NAMESPACE,
        workflowId
      );
      setPermissions(stored ?? NO_PERMISSIONS);
    } finally {
      setLoading(false);
    }
  }, [device, workflowId, workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { permissions, loading, refresh: () => void refresh() };
}
